import { useEffect, useRef, useState, useMemo } from 'react';
import {
  faCalendarAlt,
  faCaretDown,
  faCaretUp,
  faMapMarkerAlt,
  faSearch,
  faSpinner,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled, { css, keyframes } from 'styled-components';
import tw from 'twin.macro';

import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { SCREENS } from '../responsive';
import { GET_ALLOWED_LOCATIONS, GET_LOCATIONS } from '../../services/locationServices/queries';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
  IGetAllowedLocationsQuery,
  IGetAllowedLocationsQueryVariables,
  IGetLocationsQuery,
} from '../../services/locationServices/__generated__/queries.generated';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { toast } from 'react-toastify';
import { ProductType } from '../../../__generated__/graphql';
import { useBrandRules } from '../../hooks/useHostCapabilities';
import { atLeastMinimumAge, driverAgeOptions } from '../../utils/brandAge';
import { useBookingDateTime } from '../../hooks/useBookingDateTime';
import { useGetOperatingHoursByLocationAndTypeLazyQuery } from '../../services/operatingHours/__generated__/queries.generated';
import { PickupLocationType } from '../../../__generated__/graphql';
import { getSiteConfig } from '../../constants/siteConfig';
import { LocationData } from '../addressInput';
import { AddressInputSection } from '../addressInputSection';
import { ProductTypePicker } from '../productTypePicker';
import { DateRangeModal } from './DateRangeModal';
import {
  MIN_MONTHS,
  TripMode,
  daysBetween,
  monthlyFieldLabel,
  tierOf,
} from '../../helpers/monthlyTrip';
import { Colors } from '../../styles/colors';
import { DateTime } from 'luxon';
import { getTodayInTimezone } from '../../helpers/timezoneHelpers';
import { formatAddress } from '../../helpers/formatAddress';
import { PickupLocationPicker } from '../PickupLocationPicker';

// Stable empty array to prevent unnecessary re-renders
const EMPTY_BOOKED_DAYS: Array<{ from: Date; to: Date }> = [];

// Add a spinning animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const AbsoluteContainer = styled.div`
  ${tw`
    absolute
    w-full
    z-10
    flex
    flex-col
    items-center
    left-0
    px-0
    md:left-auto
    md:w-[500px]
    md:top-[10%]
    md:right-[2%]
  `}
`;

const RelativeContainer = styled.div<{ $horizontal?: boolean }>`
  ${tw`
    relative
    flex
    flex-col
    justify-center
    items-center
    mx-auto
    w-full
  `}
`;

const CardContainer = styled.div<{ $isDifferentReturn: boolean; $horizontal?: boolean }>`
  ${tw`
    transition-all
    duration-300
  `}
  ${props =>
    props.$horizontal
      ? tw`w-full bg-transparent overflow-visible`
      : css`
          width: 100%;
          padding: 16px;
          background: ${Colors.White};
          border: 1px solid ${Colors.BorderLight};
          border-radius: 0;
          overflow: visible;
          @media (min-width: ${SCREENS.md}) {
            width: 100%;
            padding: 24px;
            max-width: 500px;
            border-radius: 0 12px 12px 12px;
          }
        `}
`;

const SectionContainer = styled.div<{ $horizontal?: boolean }>`
  ${tw`
    flex
  `}
  ${props =>
    props.$horizontal
      ? tw`flex-row items-center gap-2 py-3 px-3 w-full overflow-visible min-w-0 box-border`
      : tw`flex-col gap-4`}
`;

const AgeSection = styled.div`
  ${tw`
    w-full
  `}
`;

const HorizontalAgePicker = styled.div`
  flex: 0 0 120px;
  width: 120px;
  min-width: 120px;
  max-width: 120px;
  position: relative;
  z-index: 1;

  select {
    padding-left: 12px;
    padding-right: 8px;
    box-sizing: border-box;
  }
`;

const Select = styled.select`
  ${tw`
    w-full
    h-[44px]
    border
    border-border-default
    rounded-lg
    px-3
    bg-white
    cursor-pointer
  `}
  &:hover {
    ${tw`border-gray-400`}
  }
`;

const ProductTypeSection = styled.div<{ $flexRatio?: number }>`
  ${tw`flex flex-col`}
  ${({ $flexRatio = 1 }) => `flex: ${$flexRatio};`}
`;

const TabContainer = styled.div`
  ${tw`
    flex
    gap-0
    mb-0
    relative
  `}
  background: transparent;
  width: 100%;
  padding-left: 0;
  @media (min-width: ${SCREENS.md}) {
    width: 100%;
    max-width: 500px;
  }
`;

const Tab = styled.button<{ $isActive: boolean }>`
  ${tw`
    flex-1
    md:flex-none
    px-4
    md:px-6
    py-3
    text-sm
    font-medium
    transition-all
    duration-200
    cursor-pointer
    relative
    border
    border-solid
  `}
  ${props => {
    if (props.$isActive) {
      return css`
        border-bottom: none;
        border-radius: 0;
        padding-bottom: calc(0.75rem + 2px);
        margin-bottom: -2px;
        background: ${Colors.White};
        color: ${Colors.Obsidian};
        font-weight: 600;
        z-index: 20;
        border-width: 1px;
        border-color: ${Colors.BorderLight};
        @media (min-width: ${SCREENS.md}) {
          border-radius: 8px 8px 0 0;
        }
      `;
    } else {
      return css`
        border-radius: 0;
        background: ${Colors.BgSecondary};
        color: ${Colors.TextSecondary};
        border-width: 1px;
        border-color: ${Colors.BorderLight};
        border-bottom: 1px solid ${Colors.BorderLight};
        margin-bottom: 0;
        &:hover {
          background: ${Colors.ChampagneLight};
          color: ${Colors.Obsidian};
        }
        @media (min-width: ${SCREENS.md}) {
          border-radius: 8px 8px 0 0;
        }
      `;
    }
  }}
`;

const LocationSection = styled.div<{ $hidden?: boolean; $horizontal?: boolean }>`
  ${props => props.$hidden && tw`hidden`}
  ${props => (props.$horizontal ? tw`flex flex-col flex-1 min-w-0` : tw``)}
  ${props => !props.$horizontal && ``}
`;

const DateSection = styled.div<{ $horizontal?: boolean }>`
  ${tw`
    flex
  `}
  ${props =>
    props.$horizontal ? tw`flex-row items-center gap-2 flex-shrink-0` : tw`flex-col w-full gap-4`}
`;

const LocationPicker = styled.div`
  ${tw`relative`}
`;
const ReturnLocationPicker = styled.div`
  ${tw`relative w-full mt-2`}
`;

const LocationInput = styled.div<{ $isLoading?: boolean; $horizontal?: boolean }>`
  ${tw`
    h-[44px]
    border
    border-solid
    border-border-default
    rounded-lg
    pl-3
    flex
    items-center
    cursor-pointer
    bg-white
  `}
  ${props => props.$isLoading && tw`opacity-70`}
  ${props => props.$horizontal && tw`w-full min-w-0 overflow-hidden`}
  &:hover {
    ${tw`border-gray-400`}
  }
`;

const Icon = styled.span`
  color: ${Colors.Champagne};
  ${tw`
    fill-current
    text-xs
    md:text-base
    mr-1
    md:mr-3
  `};
  position: relative;
  z-index: 1;
`;

const SmallIcon = styled.span`
  ${tw`
    text-gray-100
    fill-current
    text-xs
    md:text-base
    ml-1
    pr-2
  `};
`;

const Name = styled.span<{ $horizontal?: boolean }>`
  ${tw`
    text-xs
    md:text-sm
    cursor-pointer
    select-none
  `};
  ${props => (props.$horizontal ? tw`text-text-primary` : tw`text-text-primary`)}
  ${props =>
    props.$horizontal &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  `}
`;

const DateCalendar = styled(Calendar)`
  ${tw`w-full max-w-md border rounded-lg p-4 box-border`}
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 50;
  background: white;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
` as any;

const LocationLabel = styled.div<{ $horizontal?: boolean }>`
  ${tw`
    text-sm
    font-medium
    mb-1
  `}
  color: ${props => (props.$horizontal ? Colors.Champagne : Colors.TextSecondary)};
`;

const DatePickerContainer = styled.div<{ $horizontal?: boolean }>`
  ${tw`
    relative
    flex
    justify-between
    items-center
    h-[44px]
    pl-3
    cursor-pointer
    bg-white
  `}
  border: 1px solid ${Colors.BorderLight};
  border-radius: 8px;
  box-sizing: border-box;
  ${props =>
    props.$horizontal
      ? css`
          width: 158px;
          min-width: 158px;
          max-width: 158px;
          flex-shrink: 0;
          overflow: hidden;
          gap: 4px;
          > div:first-child {
            display: flex;
            align-items: center;
            min-width: 0;
            flex: 1;
            overflow: hidden;
          }
        `
      : tw`flex-1`}
  &:hover {
    border-color: ${Colors.Obsidian};
  }
`;

/* Spec 028: under the dates, how the trip was picked by months. Opens the picker. */
const MonthsLine = styled.button`
  ${tw`text-xs font-medium text-left`}
  color: var(--color-text-secondary);
  text-decoration: underline;
  text-underline-offset: 3px;
  &:hover {
    color: var(--color-text-primary);
  }
`;

const ValidationError = styled.div<{ $horizontal?: boolean }>`
  ${tw`
    text-red-500
    text-xs
    mt-1
  `}
  ${props => props.$horizontal && tw`text-[10px]`}
`;

const DateTimeContainer = styled.div<{ $horizontal?: boolean }>`
  ${tw`
    flex
    items-center
    gap-2
    relative
  `}
  ${props => (props.$horizontal ? tw`w-auto` : tw`w-full`)}
`;

const TimePickerContainer = styled.div<{ $horizontal?: boolean }>`
  ${tw`
    flex
    flex-row
    items-center
    pl-3
    bg-white
    cursor-pointer
    h-[44px]
    justify-between
    relative
  `}
  border: 1px solid ${Colors.BorderLight};
  border-radius: 8px;
  box-sizing: border-box;
  ${props =>
    props.$horizontal
      ? css`
          width: 150px;
          min-width: 150px;
          max-width: 150px;
          flex-shrink: 0;
          overflow: hidden;
          padding-left: 0;
          z-index: 0;
          > span:first-child {
            position: absolute;
            left: 10px;
            margin: 0;
            z-index: 1;
            pointer-events: none;
          }
        `
      : tw`w-[140px]`}
  &:hover {
    border-color: ${Colors.Obsidian};
  }
`;

const TimePickerButton = styled.select<{ $horizontal?: boolean }>`
  ${tw`
    w-full
    cursor-pointer
    h-[44px]
    pl-10
    justify-between
    absolute
    z-0
    right-0
    top-0
  `}
  border: none;
  border-radius: 8px;
  background: transparent;
  outline: none;
  box-sizing: border-box;
  &:focus {
    outline: none;
  }
  ${props =>
    props.$horizontal &&
    css`
      position: relative;
      inset: auto;
      width: 100%;
      max-width: 100%;
      height: 44px;
      padding-left: 32px;
      padding-right: 4px;
    `}
`;

const CheckboxContainer = styled.div`
  ${tw`
    flex
    items-center
    gap-2
    mt-2
  `}
`;

const Checkbox = styled.input`
  ${tw`cursor-pointer`}
`;

const CheckboxLabel = styled.label`
  ${tw`text-sm cursor-pointer`}
  color: ${Colors.TextSecondary};
`;

const Button = styled.button<{
  $isDisabled?: boolean;
  $isLoading?: boolean;
  $horizontal?: boolean;
  $inline?: boolean;
}>`
  ${tw`
    flex
    items-center
    justify-center
    font-semibold
    border-none
    transition-colors
    duration-200
    cursor-pointer
    flex-shrink-0
  `}
  border-radius: 8px;
  ${props =>
    props.$horizontal
      ? css`
          ${tw`px-3 py-3 h-[44px] w-[44px] relative z-0`}
          background-color: ${Colors.Obsidian};
          color: white;
          &:hover {
            background-color: ${Colors.ObsidianHover};
          }
        `
      : props.$inline
        ? css`
            ${tw`w-full h-[44px]`}
            background-color: ${props.$isLoading ? Colors.ObsidianHover : Colors.Obsidian};
            color: white;
            &:hover:not(:disabled) {
              background-color: ${Colors.ObsidianHover};
            }
          `
        : css`
            ${tw`w-full h-[52px] mt-4`}
            background-color: ${props.$isLoading ? Colors.ObsidianHover : Colors.Obsidian};
            color: white;
            &:hover:not(:disabled) {
              background-color: ${Colors.ObsidianHover};
            }
          `}
  cursor: ${props => {
    if (props.$isLoading) return 'wait';
  }};
  &:disabled {
    background-color: ${Colors.Gray200};
    color: ${Colors.Gray400};
    cursor: not-allowed;
  }
`;

const SearchText = styled.span`
  ${tw`
    pl-3
    text-base
    font-semibold
  `}
  color: white;
`;

const SpinnerIcon = styled(FontAwesomeIcon)`
  animation: ${spin} 1s linear infinite;
  margin-right: 8px;
`;

// Generate time options from operating hours
const generateTimeOptionsFromHours = (start: string, end: string) => {
  const options = [];
  const [startHour, startMinute] = start.split(':').map(Number);
  const [endHour, endMinute] = end.split(':').map(Number);

  const startTime = startHour * 60 + startMinute;
  const endTime = endHour * 60 + endMinute;

  // Generate options in 30-minute increments within the operating hours
  // The closing time itself is not a pickup or return slot (same rule as operatingHours.ts and Flutter).
  for (let time = startTime; time < endTime; time += 30) {
    const hour = Math.floor(time / 60);
    const minute = time % 60;

    const hourFormatted = hour.toString().padStart(2, '0');
    const minuteFormatted = minute.toString().padStart(2, '0');
    const timeValue = `${hourFormatted}:${minuteFormatted}`;
    const displayTime = `${hour % 12 || 12}:${minuteFormatted} ${hour < 12 ? 'AM' : 'PM'}`;

    options.push({ value: timeValue, label: displayTime });
  }

  return options;
};

// Format time for display
const formatTimeDisplay = (time: string) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  return `${hour % 12 || 12}:${minutes} ${hour < 12 ? 'AM' : 'PM'}`;
};

// Update BookCardProps interface to include absolute prop
interface BookCardProps {
  initialSearchParams?: {
    locationId?: number;
    returnLocationId?: number;
    startDate?: Date;
    returnDate?: Date;
    startTime?: string;
    returnTime?: string;
    driverAge?: number;
    type?: ProductType;
    searchMode?: 'location' | 'address';
    address?: string;
    latitude?: number;
    longitude?: number;
    timezone?: string;
    /** Spec 028: the trip was picked by months (`?mode=months&months=N`). */
    tripMode?: TripMode;
    months?: number;
  };
  absolute?: boolean;
  locationSectionHidden?: boolean;
  horizontal?: boolean;
  onSearchComplete?: () => void;
  syncUrlOnChange?: boolean;
}

// Add absolute prop with default value of false
export function BookCard({
  initialSearchParams = {},
  absolute = false,
  locationSectionHidden = false,
  horizontal = false,
  onSearchComplete,
  syncUrlOnChange = false,
}: BookCardProps) {
  const [isStartCalendarOpen, setStartCalendarOpen] = useState(false);
  const [isReturnCalendarOpen, setReturnCalendarOpen] = useState(false);
  const [isPickupLocationOpen, setPickupLocationOpen] = useState(false);
  const [isReturnLocationOpen, setReturnLocationOpen] = useState(false);
  const [selectedPickupLocation, setSelectedPickupLocation] = useState<
    IGetLocationsQuery['locations'][number] | null
  >(null);
  const [selectedReturnLocation, setSelectedReturnLocation] = useState<
    IGetLocationsQuery['locations'][number] | null
  >(null);
  const [locationManuallyChanged, setLocationManuallyChanged] = useState(false);
  const [differentReturnLocation, setDifferentReturnLocation] = useState(
    initialSearchParams.returnLocationId !== undefined &&
      initialSearchParams.returnLocationId !== initialSearchParams.locationId,
  );
  const [selectedAge, setSelectedAge] = useState<number | null>(
    initialSearchParams.driverAge || 25,
  );
  // Spec 014b US-5: no age range below this site's minimum driver age.
  const { effectiveMinimumAge, monthlyRentalOffered } = useBrandRules();
  // Spec 028: Months mode in the trip picker. The return time follows the pickup time, so the
  // day count is whole (`getTotalDays` rounds any extra minutes up to a day).
  const [tripMode, setTripMode] = useState<TripMode>(
    monthlyRentalOffered && initialSearchParams.tripMode === 'months' ? 'months' : 'dates',
  );
  const [months, setMonths] = useState<number>(initialSearchParams.months ?? MIN_MONTHS);
  const monthsPicked = monthlyRentalOffered && tripMode === 'months';
  const tripParams = monthsPicked ? { mode: 'months', months } : {};
  const ageOptions = useMemo(
    () => driverAgeOptions(effectiveMinimumAge).map(option => ({ ...option, name: option.label })),
    [effectiveMinimumAge],
  );
  useEffect(() => {
    if (selectedAge !== null && selectedAge < effectiveMinimumAge) {
      setSelectedAge(atLeastMinimumAge(selectedAge, effectiveMinimumAge));
    }
  }, [selectedAge, effectiveMinimumAge]);
  const siteConfig = getSiteConfig();
  const [productType, setProductType] = useState<ProductType>(
    initialSearchParams.type || siteConfig.defaultProductType,
  );

  // New state for operating hours
  const [pickupTimeOptions, setPickupTimeOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [returnTimeOptions, setReturnTimeOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [returnDay, setReturnDay] = useState<string>('');
  const [isProductTypeOpen, setProductTypeOpen] = useState(false);
  const [searchMode, setSearchMode] = useState<'location' | 'address'>(
    initialSearchParams.searchMode ||
      (horizontal && !initialSearchParams.locationId ? 'address' : 'location'),
  );
  const [selectedAddress, setSelectedAddress] = useState<LocationData | null>(null);
  const [isDateModalOpen, setDateModalOpen] = useState(false);
  const [addressKey, setAddressKey] = useState(0); // Key to reset AddressInputSection when switching modes

  const startCalendarRef = useRef<HTMLDivElement>(null);
  const returnCalendarRef = useRef<HTMLDivElement>(null);
  const pickupLocationRef = useRef<HTMLDivElement>(null);
  const returnLocationRef = useRef<HTMLDivElement>(null);

  const {
    data,
    loading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations,
  } = useQuery<IGetLocationsQuery>(GET_LOCATIONS, {
    fetchPolicy: 'cache-first',
  });
  const pickupCatalog = useMemo(() => data?.locations ?? [], [data]);

  // Initialize locations from the query params when data loads
  // Only reset if user hasn't manually changed the location
  useEffect(() => {
    if (data?.locations && initialSearchParams.locationId && !locationManuallyChanged) {
      const pickupLoc = data.locations.find(loc => loc.id === initialSearchParams.locationId);

      if (pickupLoc) {
        setSelectedPickupLocation(pickupLoc);
      }

      if (
        initialSearchParams.returnLocationId &&
        initialSearchParams.returnLocationId !== initialSearchParams.locationId
      ) {
        const returnLoc = data.locations.find(
          loc => loc.id === initialSearchParams.returnLocationId,
        );

        if (returnLoc) {
          setSelectedReturnLocation(returnLoc);
          setDifferentReturnLocation(true);
        }
      } else if (pickupLoc) {
        setSelectedReturnLocation(pickupLoc);
      }
    }
  }, [
    data,
    initialSearchParams.locationId,
    initialSearchParams.returnLocationId,
    locationManuallyChanged,
  ]);

  // Initialize selectedAddress from URL params when address search mode is active
  useEffect(() => {
    if (
      searchMode === 'address' &&
      initialSearchParams.address &&
      initialSearchParams.latitude !== undefined &&
      initialSearchParams.longitude !== undefined
    ) {
      setSelectedAddress({
        address: initialSearchParams.address,
        latitude: initialSearchParams.latitude,
        longitude: initialSearchParams.longitude,
        formattedAddress: initialSearchParams.address,
        timezone: initialSearchParams.timezone || undefined,
      });
    }
  }, [
    searchMode,
    initialSearchParams.address,
    initialSearchParams.latitude,
    initialSearchParams.longitude,
    initialSearchParams.timezone,
  ]);

  // Get operating hours query
  const [getOperatingHours, { data: operatingHoursData, loading: operatingHoursLoading }] =
    useGetOperatingHoursByLocationAndTypeLazyQuery();

  const pickupForReturnRef = useRef<IGetLocationsQuery['locations'][number] | null>(null);
  pickupForReturnRef.current = selectedPickupLocation;

  const [allowedLocations, setAllowedLocations] = useState<
    IGetAllowedLocationsQuery['getAllowedLocations']
  >([]);

  const [getAllowedLocations, { loading: allowedLocationsLoading }] = useLazyQuery<
    IGetAllowedLocationsQuery,
    IGetAllowedLocationsQueryVariables
  >(GET_ALLOWED_LOCATIONS, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const pickup = pickupForReturnRef.current;
      const allowed = data.getAllowedLocations.filter(
        location => location.isActive && location.toLocation.id !== pickup?.id,
      );
      setAllowedLocations(allowed);
      // Spec 022 FR-019: a return location the new pickup still allows stays.
      setSelectedReturnLocation(current =>
        current &&
        pickup &&
        current.id !== pickup.id &&
        !allowed.some(location => location.toLocation.id === current.id)
          ? pickup
          : current,
      );
    },
  });
  const returnCatalog = useMemo(
    () =>
      allowedLocations.map(
        location => location.toLocation as IGetLocationsQuery['locations'][number],
      ),
    [allowedLocations],
  );

  useEffect(() => {
    if (selectedPickupLocation && selectedPickupLocation.id) {
      getAllowedLocations({
        variables: { locationId: selectedPickupLocation.id },
      });

      // Fetch operating hours when location is selected
      getOperatingHours({
        variables: {
          locationId: selectedPickupLocation.id,
          PickupLocationType: PickupLocationType.Home,
        },
      });
    }
  }, [selectedPickupLocation]);

  useEffect(() => {
    if (allowedLocations.length === 0) {
      setDifferentReturnLocation(false);
    }
  }, [allowedLocations]);

  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Spec 022: the location picker's modal renders in a body portal.
      const inPortal = !!(event.target as Element | null)?.closest?.('[data-portal-safe]');
      if (startCalendarRef.current && !startCalendarRef.current.contains(event.target as Node)) {
        setStartCalendarOpen(false);
      }
      if (returnCalendarRef.current && !returnCalendarRef.current.contains(event.target as Node)) {
        setReturnCalendarOpen(false);
      }
      if (
        !inPortal &&
        pickupLocationRef.current &&
        !pickupLocationRef.current.contains(event.target as Node)
      ) {
        setPickupLocationOpen(false);
      }
      if (
        !inPortal &&
        returnLocationRef.current &&
        !returnLocationRef.current.contains(event.target as Node)
      ) {
        setReturnLocationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleStartDateCalendar = () => {
    setStartCalendarOpen(true);
    if (isReturnCalendarOpen) setReturnCalendarOpen(false);
  };

  const toggleReturnDateCalendar = () => {
    setReturnCalendarOpen(true);
    if (isStartCalendarOpen) setStartCalendarOpen(false);
  };

  const toggleProductType = () => {
    setProductTypeOpen(!isProductTypeOpen);
    setPickupLocationOpen(false);
    setReturnLocationOpen(false);
    setStartCalendarOpen(false);
    setReturnCalendarOpen(false);
  };

  const handleProductTypeChange = (type: ProductType) => {
    setProductType(type);
  };

  /** Spec 022: the return stays when it is a different location (checked once the new list loads). */
  const choosePickupLocation = (location: IGetLocationsQuery['locations'][number]) => {
    setSelectedPickupLocation(location);
    setLocationManuallyChanged(true);
    setSelectedReturnLocation(current =>
      differentReturnLocation && current && current.id !== location.id ? current : location,
    );
  };

  const togglePickupLocation = () => {
    setPickupLocationOpen(!isPickupLocationOpen);
    setProductTypeOpen(false);
    setReturnLocationOpen(false);
    setStartCalendarOpen(false);
    setReturnCalendarOpen(false);
  };

  const toggleReturnLocation = () => {
    if (allowedLocations.length === 0) {
      toast.error('No return locations are configured for this pickup location.', {
        position: 'top-center',
      });
      return;
    }
    setReturnLocationOpen(!isReturnLocationOpen);
    setPickupLocationOpen(false);
    setStartCalendarOpen(false);
    setReturnCalendarOpen(false);
  };

  const {
    startDate,
    endDate,
    startTime,
    endTime,
    validationError,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
    startAtOptions,
    endAtOptions,
  } = useBookingDateTime({
    timezone:
      searchMode === 'address'
        ? (selectedAddress?.timezone ?? undefined)
        : (selectedPickupLocation?.timezone ?? undefined),
    bookedDays: EMPTY_BOOKED_DAYS,
    initialStartDate: initialSearchParams.startDate,
    initialEndDate: initialSearchParams.returnDate,
    initialStartTime: initialSearchParams.startTime,
    initialEndTime: initialSearchParams.returnTime,
  });

  useEffect(() => {
    if (monthsPicked && startTime && endTime !== startTime) setEndTime(startTime);
  }, [monthsPicked, startTime, endTime, setEndTime]);

  const onStartDateChange = (date: Date) => {
    setStartDate(date);
    setStartCalendarOpen(false);
  };

  const onReturnDateChange = (date: Date) => {
    setEndDate(date);
    setReturnCalendarOpen(false);
  };

  // Add a function to check if required fields are completed
  const areRequiredFieldsComplete = () => {
    if (searchMode === 'address') {
      return !!(selectedAddress && startDate && endDate && selectedAge && startTime && endTime);
    }
    return !!(
      selectedPickupLocation &&
      startDate &&
      endDate &&
      selectedAge &&
      startTime &&
      endTime
    );
  };

  const handleSearch = () => {
    if (searchMode === 'address') {
      // Address-based search
      if (!selectedAddress) {
        toast.error('Please select an address', { position: 'top-center' });
        return;
      }

      if (!startDate) {
        toast.error('Pickup date is required', { position: 'top-center' });
        return;
      }

      if (!endDate) {
        toast.error('Return date is required', { position: 'top-center' });
        return;
      }

      if (!startTime) {
        toast.error('Pickup time is required', { position: 'top-center' });
        return;
      }

      if (!endTime) {
        toast.error('Return time is required', { position: 'top-center' });
        return;
      }

      if (!selectedAge) {
        toast.error('Driver age is required', { position: 'top-center' });
        return;
      }

      const query = queryString.stringify(
        {
          searchMode: 'address',
          ...tripParams,
          latitude: selectedAddress.latitude,
          longitude: selectedAddress.longitude,
          address: selectedAddress.formattedAddress,
          timezone: selectedAddress.timezone ?? 'America/Los_Angeles',
          startTime,
          returnTime: monthsPicked ? startTime : endTime,
          startDate: startDate.toISOString(),
          returnDate: endDate.toISOString(),
          driverAge: selectedAge,
          type: productType,
        },
        {
          skipNull: true,
          skipEmptyString: true,
        },
      );
      navigate({
        pathname: '/cars/guest/search',
        search: `?${query}`,
      });
      onSearchComplete?.();
      return;
    }

    // Location-based search (existing logic)
    if (!selectedPickupLocation) {
      toast.error('Pickup location is required', { position: 'top-center' });
      return;
    }

    if (!startDate) {
      toast.error('Pickup date is required', { position: 'top-center' });
      return;
    }

    if (!endDate) {
      toast.error('Return date is required', { position: 'top-center' });
      return;
    }

    if (!startTime) {
      toast.error('Pickup time is required', { position: 'top-center' });
      return;
    }

    if (!endTime) {
      toast.error('Return time is required', { position: 'top-center' });
      return;
    }

    if (!selectedAge) {
      toast.error('Driver age is required', { position: 'top-center' });
      return;
    }

    // Check if time options are available
    if (filteredPickupTimeOptions.length === 0) {
      toast.error(
        isToday(startDate)
          ? 'Location is closed for pickup today. Please select a different date.'
          : 'Location is not available for pickup on selected date',
        {
          position: 'top-center',
        },
      );
      return;
    }

    if (filteredReturnTimeOptions.length === 0) {
      toast.error(
        isToday(endDate)
          ? 'Location is closed for return today. Please select a different date.'
          : 'Location is not available for return on selected date',
        {
          position: 'top-center',
        },
      );
      return;
    }

    const query = queryString.stringify(
      {
        searchMode: 'location',
        ...tripParams,
        timezone: selectedPickupLocation?.timezone ?? 'America/Los_Angeles',
        locationId: selectedPickupLocation.id,
        startTime,
        returnTime: monthsPicked ? startTime : endTime,
        startDate: startDate.toISOString(),
        returnDate: endDate.toISOString(),
        returnLocationId: selectedReturnLocation?.id ?? selectedPickupLocation.id,
        driverAge: selectedAge,
        type: productType,
      },
      {
        skipNull: true,
        skipEmptyString: true,
      },
    );
    navigate({
      pathname: '/cars/guest/search',
      search: `?${query}`,
    });
    onSearchComplete?.();
  };

  useEffect(() => {
    if (!syncUrlOnChange) return;
    if (!startDate || !endDate || !startTime || !endTime || !selectedAge) return;

    if (searchMode === 'address') {
      if (!selectedAddress) return;
      const query = queryString.stringify(
        {
          searchMode: 'address',
          ...tripParams,
          latitude: selectedAddress.latitude,
          longitude: selectedAddress.longitude,
          address: selectedAddress.formattedAddress,
          timezone: selectedAddress.timezone ?? 'America/Los_Angeles',
          startTime,
          returnTime: monthsPicked ? startTime : endTime,
          startDate: startDate.toISOString(),
          returnDate: endDate.toISOString(),
          driverAge: selectedAge,
          type: productType,
        },
        {
          skipNull: true,
          skipEmptyString: true,
        },
      );
      const nextSearch = `?${query}`;
      if (location.pathname === '/cars/guest/search' && location.search === nextSearch) {
        return;
      }
      navigate(
        {
          pathname: '/cars/guest/search',
          search: nextSearch,
        },
        { replace: true },
      );
      return;
    }

    if (!selectedPickupLocation) return;
    const query = queryString.stringify(
      {
        searchMode: 'location',
        ...tripParams,
        timezone: selectedPickupLocation?.timezone ?? 'America/Los_Angeles',
        locationId: selectedPickupLocation.id,
        startTime,
        returnTime: monthsPicked ? startTime : endTime,
        startDate: startDate.toISOString(),
        returnDate: endDate.toISOString(),
        returnLocationId: selectedReturnLocation?.id ?? selectedPickupLocation.id,
        driverAge: selectedAge,
        type: productType,
      },
      {
        skipNull: true,
        skipEmptyString: true,
      },
    );
    const nextSearch = `?${query}`;
    if (location.pathname === '/cars/guest/search' && location.search === nextSearch) {
      return;
    }
    navigate(
      {
        pathname: '/cars/guest/search',
        search: nextSearch,
      },
      { replace: true },
    );
  }, [
    syncUrlOnChange,
    searchMode,
    selectedAddress,
    selectedPickupLocation,
    selectedReturnLocation,
    startDate,
    endDate,
    startTime,
    endTime,
    selectedAge,
    productType,
    location.pathname,
    location.search,
    navigate,
  ]);

  const {
    address: pickupAddress,
    city: pickupCity,
    state: pickupState,
    zipCode: pickupZipCode,
  } = selectedPickupLocation || {};
  const {
    address: returnAddress,
    city: returnCity,
    state: returnState,
    zipCode: returnZipCode,
  } = selectedReturnLocation || {};

  // Update time options when dates change or location changes
  useEffect(() => {
    if (selectedPickupLocation && startDate) {
      // Fetch operating hours for the selected location
      if (selectedPickupLocation.id) {
        getOperatingHours({
          variables: {
            locationId: Number(selectedPickupLocation.id),
            PickupLocationType: PickupLocationType.Home,
          },
        });
      }
    }
  }, [selectedPickupLocation, getOperatingHours]);

  // Update pickup time options when operating hours data changes
  useEffect(() => {
    if (operatingHoursData?.operatingHoursByLocationAndType && startDate) {
      const hours = operatingHoursData.operatingHoursByLocationAndType;

      // Check top-level alwaysAvailable first - if true, all days are 24/7
      if (hours.alwaysAvailable) {
        setPickupTimeOptions(generateTimeOptionsFromHours('00:00', '23:30'));
        return;
      }

      const dayOfWeek = startDate.getDay();
      const dayNames = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ];
      const dayName = dayNames[dayOfWeek];

      // Check if the day is available
      const isDayAvailable = hours[`${dayName}Available` as keyof typeof hours] as boolean;
      const isDayAlwaysAvailable = hours[
        `${dayName}AlwaysAvailable` as keyof typeof hours
      ] as boolean;

      if (isDayAvailable) {
        if (isDayAlwaysAvailable) {
          // If day is always available, use full day hours
          setPickupTimeOptions(generateTimeOptionsFromHours('00:00', '23:30'));
        } else {
          // Use specific hours for the day
          const start = hours[`${dayName}Start` as keyof typeof hours] as string;
          const end = hours[`${dayName}End` as keyof typeof hours] as string;
          if (start && end) {
            setPickupTimeOptions(generateTimeOptionsFromHours(start, end));
          } else {
            setPickupTimeOptions([]);
          }
        }
      } else {
        // Day not available
        setPickupTimeOptions([]);
        toast.error(
          `Location is not available on ${dayName.charAt(0).toUpperCase() + dayName.slice(1)}`,
          {
            position: 'top-center',
          },
        );
      }
    } else if (!operatingHoursData && startDate) {
      // Default to full day if no operating hours are set (e.g. searching by address)
      setPickupTimeOptions(generateTimeOptionsFromHours('00:00', '23:30'));
    }
  }, [operatingHoursData, startDate]);

  // Similar update for return time options
  useEffect(() => {
    if (operatingHoursData?.operatingHoursByLocationAndType && endDate) {
      const hours = operatingHoursData.operatingHoursByLocationAndType;

      // Check top-level alwaysAvailable first - if true, all days are 24/7
      if (hours.alwaysAvailable) {
        setReturnTimeOptions(generateTimeOptionsFromHours('00:00', '23:30'));
        return;
      }

      const dayOfWeek = endDate.getDay();
      const dayNames = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ];
      const dayName = dayNames[dayOfWeek];

      const isDayAvailable = hours[`${dayName}Available` as keyof typeof hours] as boolean;
      const isDayAlwaysAvailable = hours[
        `${dayName}AlwaysAvailable` as keyof typeof hours
      ] as boolean;

      if (isDayAvailable) {
        if (isDayAlwaysAvailable) {
          setReturnTimeOptions(generateTimeOptionsFromHours('00:00', '23:30'));
        } else {
          const start = hours[`${dayName}Start` as keyof typeof hours] as string;
          const end = hours[`${dayName}End` as keyof typeof hours] as string;
          if (start && end) {
            setReturnTimeOptions(generateTimeOptionsFromHours(start, end));
          } else {
            setReturnTimeOptions([]);
          }
        }
      } else {
        setReturnTimeOptions([]);
        toast.error(
          `Location is not available for return on ${dayName.charAt(0).toUpperCase() + dayName.slice(1)}`,
          {
            position: 'top-center',
          },
        );
      }
    } else if (!operatingHoursData && endDate) {
      // Default to full day if no operating hours are set
      setReturnTimeOptions(generateTimeOptionsFromHours('00:00', '23:30'));
    }
  }, [operatingHoursData, endDate]);

  // Update the useEffect that fetches operating hours to trigger on location change
  useEffect(() => {
    if (selectedPickupLocation?.id) {
      getOperatingHours({
        variables: {
          locationId: Number(selectedPickupLocation.id),
          PickupLocationType: PickupLocationType.Home,
        },
      });
    }
  }, [selectedPickupLocation, getOperatingHours]);

  // For return location, we need a separate effect if different return location is selected
  useEffect(() => {
    if (differentReturnLocation && selectedReturnLocation?.id) {
      // If using a different return location, fetch its operating hours when needed
      getOperatingHours({
        variables: {
          locationId: Number(selectedReturnLocation.id),
          PickupLocationType: PickupLocationType.Home,
        },
      });
    }
  }, [differentReturnLocation, selectedReturnLocation, getOperatingHours]);

  // Calculate the intersection of operating hours and available times (filtered by current time)
  // This is needed because:
  // - pickupTimeOptions comes from location operating hours (e.g., 8 AM - 6 PM)
  // - startAtOptions comes from useBookingDateTime which filters for times after now + 1 hour
  // - When it's late in the day, the intersection can be empty
  // Using useMemo to prevent infinite re-renders
  const filteredPickupTimeOptions = useMemo(
    () => pickupTimeOptions.filter(option => startAtOptions.includes(option.label)),
    [pickupTimeOptions, startAtOptions],
  );

  const filteredReturnTimeOptions = useMemo(
    () => returnTimeOptions.filter(option => endAtOptions.includes(option.label)),
    [returnTimeOptions, endAtOptions],
  );

  // Check if a date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  // Check if TODAY specifically has available times (independent of selected date)
  // This is used to disable today in the calendar when no times are available
  const isTodayAvailable = useMemo(() => {
    if (searchMode !== 'location') return true;
    if (!selectedPickupLocation) return true;
    if (!operatingHoursData?.operatingHoursByLocationAndType) return true;

    const today = new Date();
    const hours = operatingHoursData.operatingHoursByLocationAndType;

    // Check top-level alwaysAvailable first - if true, all days are 24/7
    if (hours.alwaysAvailable) {
      // Still need to check if there's time left today (must be before 23:30)
      const now = new Date();
      const bufferTime = new Date(now.getTime() + 60 * 60 * 1000); // +1 hour
      const endTimeToday = new Date(today);
      endTimeToday.setHours(23, 30, 0, 0);
      return endTimeToday > bufferTime;
    }

    const dayOfWeek = today.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];

    const isDayAvailable = hours[`${dayName}Available` as keyof typeof hours] as boolean;
    if (!isDayAvailable) return false;

    const isDayAlwaysAvailable = hours[
      `${dayName}AlwaysAvailable` as keyof typeof hours
    ] as boolean;

    let endHour = '23:30';

    if (!isDayAlwaysAvailable) {
      endHour = (hours[`${dayName}End` as keyof typeof hours] as string) || '23:30';
    }

    // Check if there are any times available after current time + 1 hour buffer
    const now = new Date();
    const bufferTime = new Date(now.getTime() + 60 * 60 * 1000); // +1 hour

    const [endHourNum, endMinNum] = endHour.split(':').map(Number);
    const endTimeToday = new Date(today);
    endTimeToday.setHours(endHourNum, endMinNum, 0, 0);

    // If operating hours end before current time + buffer, today is not available
    return endTimeToday > bufferTime;
  }, [searchMode, selectedPickupLocation, operatingHoursData]);

  // Auto-select the first available time when filtered options change
  // Only react to option changes, not time changes, to avoid infinite loops
  const prevFilteredPickupLabelsRef = useRef<string>('');
  const prevFilteredReturnLabelsRef = useRef<string>('');

  // Auto-advance to next day if today has no available times
  const hasAutoAdvancedRef = useRef<string>('');

  // Track previous timezone to detect changes in address mode
  const prevTimezoneRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (searchMode !== 'location') return;
    if (!selectedPickupLocation) return;
    if (operatingHoursLoading) return;

    // Create a unique key for this date + location combination
    const advanceKey = `${startDate.toDateString()}-${selectedPickupLocation.id}`;

    // Only check once per date+location combination to avoid loops
    if (hasAutoAdvancedRef.current === advanceKey) return;

    // If today is selected and no times are available, advance to tomorrow
    if (
      isToday(startDate) &&
      pickupTimeOptions.length > 0 && // Operating hours loaded
      filteredPickupTimeOptions.length === 0 // But no times available after filtering
    ) {
      hasAutoAdvancedRef.current = advanceKey;

      const tomorrow = new Date(startDate);
      tomorrow.setDate(tomorrow.getDate() + 1);
      setStartDate(tomorrow);

      // Also advance end date if it's the same day or before
      if (endDate <= startDate) {
        const newEndDate = new Date(tomorrow);
        newEndDate.setDate(newEndDate.getDate() + 1);
        setEndDate(newEndDate);
      }

      toast.info('Today is not available. Showing next available date.', {
        position: 'top-center',
        toastId: 'auto-advance-date',
      });
    }
  }, [
    searchMode,
    selectedPickupLocation,
    operatingHoursLoading,
    startDate,
    endDate,
    pickupTimeOptions,
    filteredPickupTimeOptions,
    setStartDate,
    setEndDate,
  ]);

  // Check if selected date is in the past when timezone changes (address mode)
  useEffect(() => {
    if (searchMode !== 'address') return;
    if (!selectedAddress?.timezone) return;
    if (!startDate) return;

    const currentTimezone = selectedAddress.timezone;

    // Only check if timezone actually changed
    if (prevTimezoneRef.current === currentTimezone) return;

    // Update the ref
    prevTimezoneRef.current = currentTimezone;

    // Check if the selected startDate is in the past relative to the new timezone
    const nowInNewTimezone = DateTime.now().setZone(currentTimezone);
    const selectedInNewTimezone = DateTime.fromJSDate(startDate).setZone(currentTimezone);

    // Compare just the year, month, day components to avoid timestamp precision issues
    const todayYear = nowInNewTimezone.year;
    const todayMonth = nowInNewTimezone.month;
    const todayDay = nowInNewTimezone.day;

    const selectedYear = selectedInNewTimezone.year;
    const selectedMonth = selectedInNewTimezone.month;
    const selectedDay = selectedInNewTimezone.day;

    // Check if selected date is strictly BEFORE today in the new timezone
    const isSelectedDateInPast =
      selectedYear < todayYear ||
      (selectedYear === todayYear && selectedMonth < todayMonth) ||
      (selectedYear === todayYear && selectedMonth === todayMonth && selectedDay < todayDay);

    if (isSelectedDateInPast) {
      // Date is in the past, show toast and advance to today
      const formattedDate = startDate.toLocaleDateString();

      // Create a JavaScript Date that will display correctly in the user's browser
      // by extracting date components from the target timezone
      // Use noon to avoid timezone edge cases when displaying
      const todayDate = new Date(todayYear, todayMonth - 1, todayDay, 12, 0, 0);
      setStartDate(todayDate);

      // Also advance end date if it's the same day or before
      if (endDate <= startDate) {
        const newEndDate = new Date(todayYear, todayMonth - 1, todayDay + 1, 12, 0, 0);
        setEndDate(newEndDate);
      }
    }
  }, [searchMode, selectedAddress?.timezone, startDate, endDate, setStartDate, setEndDate]);

  useEffect(() => {
    if (searchMode !== 'location') return;

    const currentLabelsStr = filteredPickupTimeOptions.map(opt => opt.label).join(',');

    // Only run if the options actually changed
    if (currentLabelsStr !== prevFilteredPickupLabelsRef.current) {
      prevFilteredPickupLabelsRef.current = currentLabelsStr;

      // If current startTime is not in the available options, auto-select first available
      if (
        filteredPickupTimeOptions.length > 0 &&
        !filteredPickupTimeOptions.some(opt => opt.label === startTime)
      ) {
        setStartTime(filteredPickupTimeOptions[0].label);
      }
    }
  }, [filteredPickupTimeOptions, searchMode, startTime, setStartTime]);

  // Auto-select the first available time in address mode when startAtOptions change
  const prevStartAtOptionsRef = useRef<string>('');
  useEffect(() => {
    if (searchMode !== 'address') return;
    if (!startAtOptions || startAtOptions.length === 0) return;

    const currentOptionsStr = startAtOptions.join(',');

    // Only run if the options actually changed
    if (currentOptionsStr !== prevStartAtOptionsRef.current) {
      prevStartAtOptionsRef.current = currentOptionsStr;

      // If current startTime is not in the available options, auto-select first available
      if (startAtOptions.length > 0 && !startAtOptions.includes(startTime)) {
        setStartTime(startAtOptions[0]);
      }
    }
  }, [startAtOptions, searchMode, startTime, setStartTime]);

  useEffect(() => {
    if (searchMode !== 'location') return;

    const currentLabelsStr = filteredReturnTimeOptions.map(opt => opt.label).join(',');

    // Only run if the options actually changed
    if (currentLabelsStr !== prevFilteredReturnLabelsRef.current) {
      prevFilteredReturnLabelsRef.current = currentLabelsStr;

      // If current endTime is not in the available options, auto-select first available
      if (
        filteredReturnTimeOptions.length > 0 &&
        !filteredReturnTimeOptions.some(opt => opt.label === endTime)
      ) {
        setEndTime(filteredReturnTimeOptions[0].label);
      }
    }
  }, [filteredReturnTimeOptions, searchMode]);

  // Check if any loading state is active
  const isAnyLoading = locationsLoading || operatingHoursLoading || allowedLocationsLoading;

  // Add a function to check if search is currently loading
  const isSearchLoading = () => {
    return operatingHoursLoading || allowedLocationsLoading;
  };

  // Function to prevent event propagation for calendar navigation
  const handleCalendarNavigation = (event: React.MouseEvent) => {
    // Check if the click is on a navigation button
    const target = event.target as HTMLElement;
    if (
      target &&
      (target.classList.contains('react-calendar__navigation__arrow') ||
        target.classList.contains('react-calendar__navigation__label') ||
        target.closest('.react-calendar__navigation'))
    ) {
      event.stopPropagation();
    }
  };

  // Only modify the return statement at the end to use the Container conditionally
  const Container = absolute ? AbsoluteContainer : RelativeContainer;

  return (
    <Container $horizontal={horizontal} data-testid="booking-card-container">
      {!horizontal && (
        <TabContainer>
          <Tab
            $isActive={searchMode === 'location'}
            data-testid="booking-location-tab"
            onClick={() => {
              setSearchMode('location');
              setSelectedAddress(null);
              setAddressKey(prev => prev + 1); // Reset AddressInputSection
            }}
          >
            Pickup Location
          </Tab>
          <Tab
            $isActive={searchMode === 'address'}
            data-testid="booking-address-tab"
            onClick={() => {
              setSearchMode('address');
              setSelectedPickupLocation(null);
              setSelectedReturnLocation(null);
              setDifferentReturnLocation(false);
            }}
          >
            Enter Address
          </Tab>
        </TabContainer>
      )}
      <CardContainer $isDifferentReturn={differentReturnLocation} $horizontal={horizontal}>
        <SectionContainer $horizontal={horizontal}>
          <LocationSection $hidden={locationSectionHidden} $horizontal={horizontal}>
            {horizontal && searchMode === 'address' && (
              <AddressInputSection
                key={addressKey}
                initialValue={initialSearchParams.address || ''}
                placeholder="Enter an address..."
                horizontal={true}
                onLocationSelect={location => {
                  setSelectedAddress(location);
                }}
              />
            )}
            {horizontal && searchMode === 'location' && (
              <div
                ref={pickupLocationRef}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <LocationInput
                  onClick={togglePickupLocation}
                  $isLoading={locationsLoading}
                  $horizontal={horizontal}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 0,
                      flex: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Icon>
                      {locationsLoading ? (
                        <SpinnerIcon icon={faSpinner} />
                      ) : (
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                      )}
                    </Icon>
                    <Name $horizontal={horizontal}>
                      {selectedPickupLocation
                        ? formatAddress({
                            address: pickupAddress,
                            city: pickupCity,
                            state: pickupState,
                            zipCode: pickupZipCode,
                          })
                        : locationsLoading
                          ? 'Loading locations...'
                          : 'Choose a pickup location'}
                    </Name>
                  </div>
                  <SmallIcon>
                    <FontAwesomeIcon icon={isPickupLocationOpen ? faCaretUp : faCaretDown} />
                  </SmallIcon>
                </LocationInput>
                <PickupLocationPicker
                  kind="pickup"
                  open={isPickupLocationOpen}
                  onClose={() => setPickupLocationOpen(false)}
                  locations={pickupCatalog}
                  value={selectedPickupLocation}
                  onSelect={choosePickupLocation}
                  loading={locationsLoading}
                  error={!!locationsError}
                  onRetry={() => refetchLocations()}
                  forceModal
                />
              </div>
            )}

            {!horizontal && searchMode === 'location' && (
              <>
                <LocationPicker ref={pickupLocationRef}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <LocationLabel>Pickup location</LocationLabel>
                    <CheckboxContainer style={{ marginTop: 0 }}>
                      <Checkbox
                        type="checkbox"
                        id="differentReturn"
                        checked={differentReturnLocation}
                        data-testid="booking-different-return-checkbox"
                        onChange={e => {
                          setDifferentReturnLocation(e.target.checked);
                          if (!e.target.checked) {
                            setSelectedReturnLocation(selectedPickupLocation);
                          }
                        }}
                      />
                      <CheckboxLabel htmlFor="differentReturn">
                        Return to a different location
                      </CheckboxLabel>
                    </CheckboxContainer>
                  </div>
                  <LocationInput
                    onClick={togglePickupLocation}
                    $isLoading={locationsLoading}
                    data-testid="booking-pickup-location-input"
                  >
                    <div>
                      <Icon>
                        {locationsLoading ? (
                          <SpinnerIcon icon={faSpinner} />
                        ) : (
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                        )}
                      </Icon>
                      <Name>
                        {selectedPickupLocation
                          ? formatAddress({
                              address: pickupAddress,
                              city: pickupCity,
                              state: pickupState,
                              zipCode: pickupZipCode,
                            })
                          : locationsLoading
                            ? 'Loading locations...'
                            : 'Choose a pickup location'}
                      </Name>
                    </div>
                    <SmallIcon>
                      <FontAwesomeIcon icon={isPickupLocationOpen ? faCaretUp : faCaretDown} />
                    </SmallIcon>
                  </LocationInput>
                  <PickupLocationPicker
                    kind="pickup"
                    open={isPickupLocationOpen}
                    onClose={() => setPickupLocationOpen(false)}
                    locations={pickupCatalog}
                    value={selectedPickupLocation}
                    onSelect={choosePickupLocation}
                    loading={locationsLoading}
                    error={!!locationsError}
                    onRetry={() => refetchLocations()}
                  />
                </LocationPicker>

                {differentReturnLocation && (
                  <LocationPicker ref={returnLocationRef}>
                    <LocationLabel>Return location</LocationLabel>
                    <LocationInput
                      onClick={toggleReturnLocation}
                      $isLoading={allowedLocationsLoading}
                      data-testid="booking-return-location-input"
                    >
                      <Icon>
                        {allowedLocationsLoading ? (
                          <SpinnerIcon icon={faSpinner} />
                        ) : (
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                        )}
                      </Icon>
                      <Name>
                        {selectedReturnLocation
                          ? formatAddress({
                              address: returnAddress,
                              city: returnCity,
                              state: returnState,
                              zipCode: returnZipCode,
                            })
                          : allowedLocationsLoading
                            ? 'Loading locations...'
                            : 'Choose a return location'}
                      </Name>
                      <SmallIcon>
                        <FontAwesomeIcon icon={isReturnLocationOpen ? faCaretUp : faCaretDown} />
                      </SmallIcon>
                    </LocationInput>
                    <PickupLocationPicker
                      kind="return"
                      open={isReturnLocationOpen}
                      onClose={() => setReturnLocationOpen(false)}
                      locations={returnCatalog}
                      value={selectedReturnLocation}
                      onSelect={location => setSelectedReturnLocation(location)}
                      loading={allowedLocationsLoading}
                      anchor={selectedPickupLocation}
                    />
                  </LocationPicker>
                )}
              </>
            )}

            {!horizontal && searchMode === 'address' && (
              <AddressInputSection
                key={addressKey}
                initialValue={initialSearchParams.address || ''}
                placeholder="Enter an address anywhere..."
                showLabel={true}
                onLocationSelect={location => {
                  setSelectedAddress(location);
                }}
              />
            )}
          </LocationSection>

          <DateSection $horizontal={horizontal}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                minWidth: horizontal ? '0' : 'auto',
                flexShrink: 0,
              }}
            >
              <div
                style={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}
              >
                <DateTimeContainer $horizontal={horizontal}>
                  <DatePickerContainer
                    $horizontal={horizontal}
                    onClick={() => setDateModalOpen(true)}
                    data-testid="booking-pickup-date-input"
                  >
                    <div>
                      <Icon>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                      </Icon>
                      <Name $horizontal={horizontal}>
                        {startDate
                          ? startDate.toLocaleDateString()
                          : horizontal
                            ? 'Date'
                            : 'Pick Up Date'}
                      </Name>
                    </div>
                    <SmallIcon>
                      <FontAwesomeIcon icon={isDateModalOpen ? faCaretUp : faCaretDown} />
                    </SmallIcon>
                  </DatePickerContainer>

                  <TimePickerContainer $horizontal={horizontal}>
                    <Icon>
                      {operatingHoursLoading ? (
                        <SpinnerIcon icon={faSpinner} />
                      ) : (
                        <FontAwesomeIcon icon={faClock} />
                      )}
                    </Icon>
                    <TimePickerButton
                      $horizontal={horizontal}
                      value={startTime}
                      onChange={e => setStartTime(e.target.value)}
                      disabled={filteredPickupTimeOptions.length === 0 || operatingHoursLoading}
                      data-testid="booking-pickup-time-select"
                    >
                      {operatingHoursLoading ? (
                        <option value="">Loading...</option>
                      ) : filteredPickupTimeOptions.length > 0 ? (
                        filteredPickupTimeOptions.map(option => (
                          <option key={`start-${option.value}`} value={option.label}>
                            {option.label}
                          </option>
                        ))
                      ) : (
                        <option value="">Not Available</option>
                      )}
                    </TimePickerButton>
                  </TimePickerContainer>
                </DateTimeContainer>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                minWidth: horizontal ? '0' : 'auto',
                flexShrink: 0,
              }}
            >
              <div
                style={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}
              >
                <DateTimeContainer $horizontal={horizontal}>
                  <DatePickerContainer
                    $horizontal={horizontal}
                    onClick={() => setDateModalOpen(true)}
                    data-testid="booking-return-date-input"
                  >
                    <div>
                      <Icon>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                      </Icon>
                      <Name $horizontal={horizontal}>
                        {endDate
                          ? endDate.toLocaleDateString()
                          : horizontal
                            ? 'Date'
                            : 'Return Date'}
                      </Name>
                    </div>
                    <SmallIcon>
                      <FontAwesomeIcon icon={isDateModalOpen ? faCaretUp : faCaretDown} />
                    </SmallIcon>
                  </DatePickerContainer>
                  <TimePickerContainer $horizontal={horizontal}>
                    <Icon>
                      {operatingHoursLoading ? (
                        <SpinnerIcon icon={faSpinner} />
                      ) : (
                        <FontAwesomeIcon icon={faClock} size="sm" />
                      )}
                    </Icon>
                    <TimePickerButton
                      $horizontal={horizontal}
                      value={endTime}
                      onChange={e => setEndTime(e.target.value)}
                      disabled={
                        monthsPicked ||
                        filteredReturnTimeOptions.length === 0 ||
                        operatingHoursLoading
                      }
                      title={monthsPicked ? 'Same as the pickup time on a monthly trip' : undefined}
                      data-testid="booking-return-time-select"
                    >
                      {operatingHoursLoading ? (
                        <option value="">Loading...</option>
                      ) : filteredReturnTimeOptions.length > 0 ? (
                        filteredReturnTimeOptions.map(option => (
                          <option key={`return-${option.value}`} value={option.label}>
                            {option.label}
                          </option>
                        ))
                      ) : (
                        <option value="">Not Available</option>
                      )}
                    </TimePickerButton>
                  </TimePickerContainer>
                </DateTimeContainer>
              </div>
              {validationError && (
                <ValidationError $horizontal={horizontal}>{validationError}</ValidationError>
              )}
              {monthsPicked && startDate && endDate && (
                <MonthsLine
                  type="button"
                  onClick={() => setDateModalOpen(true)}
                  data-testid="booking-months-line"
                >
                  {monthlyFieldLabel(
                    months,
                    DateTime.fromJSDate(startDate).toISODate() ?? '',
                    DateTime.fromJSDate(endDate).toISODate() ?? '',
                  )}
                  {' · '}
                  {tierOf(
                    daysBetween(
                      DateTime.fromJSDate(startDate).toISODate() ?? '',
                      DateTime.fromJSDate(endDate).toISODate() ?? '',
                    ),
                  )}
                </MonthsLine>
              )}
            </div>
          </DateSection>
          {!horizontal && !siteConfig.isSingleProductSite && (
            <DateTimeContainer>
              <ProductTypeSection $flexRatio={2}>
                <ProductTypePicker
                  productType={productType}
                  onProductTypeChange={handleProductTypeChange}
                  isOpen={isProductTypeOpen}
                  onToggle={toggleProductType}
                />
              </ProductTypeSection>
              <ProductTypeSection $flexRatio={1}>
                <LocationLabel>Age</LocationLabel>
                <AgeSection>
                  <Select
                    value={selectedAge || ''}
                    onChange={e => setSelectedAge(parseInt(e.target.value))}
                    disabled={isAnyLoading}
                    data-testid="booking-age-select"
                  >
                    {ageOptions.map(age => (
                      <option key={age.name} value={age.value}>
                        {age.label}
                      </option>
                    ))}
                  </Select>
                </AgeSection>
              </ProductTypeSection>
            </DateTimeContainer>
          )}
          {!horizontal && siteConfig.isSingleProductSite && (
            <DateTimeContainer>
              <AgeSection style={{ flex: 1 }}>
                <Select
                  value={selectedAge || ''}
                  onChange={e => setSelectedAge(parseInt(e.target.value))}
                  disabled={isAnyLoading}
                >
                  {ageOptions.map(age => (
                    <option key={age.name} value={age.value}>
                      {age.label}
                    </option>
                  ))}
                </Select>
              </AgeSection>
              <Button
                onClick={handleSearch}
                $isDisabled={!areRequiredFieldsComplete()}
                $isLoading={isSearchLoading()}
                $inline
                style={{ width: '140px' }}
                data-testid="booking-search-button"
              >
                {isSearchLoading() ? (
                  <SpinnerIcon icon={faSpinner} size="lg" color="white" />
                ) : (
                  <>
                    <FontAwesomeIcon
                      icon={faSearch}
                      size="lg"
                      color={areRequiredFieldsComplete() ? 'white' : 'var(--color-border)'}
                    />
                    <SearchText>Search</SearchText>
                  </>
                )}
              </Button>
            </DateTimeContainer>
          )}
          {horizontal && (
            <HorizontalAgePicker>
              <Select
                value={selectedAge || ''}
                onChange={e => setSelectedAge(parseInt(e.target.value, 10))}
                disabled={isAnyLoading}
                aria-label="Driver age"
                data-testid="booking-age-select-horizontal"
              >
                {ageOptions.map(age => (
                  <option key={age.name} value={age.value}>
                    {age.label}
                  </option>
                ))}
              </Select>
            </HorizontalAgePicker>
          )}
          {horizontal && (
            <Button
              $horizontal={horizontal}
              onClick={handleSearch}
              $isDisabled={!areRequiredFieldsComplete()}
              $isLoading={isSearchLoading()}
            >
              {isSearchLoading() ? (
                <SpinnerIcon icon={faSpinner} size="sm" />
              ) : (
                <FontAwesomeIcon icon={faSearch} size="sm" />
              )}
            </Button>
          )}
          {!horizontal && !siteConfig.isSingleProductSite && (
            <Button
              onClick={handleSearch}
              $isDisabled={!areRequiredFieldsComplete()}
              $isLoading={isSearchLoading()}
              data-testid="booking-search-button"
            >
              {isSearchLoading() ? (
                <>
                  <SpinnerIcon icon={faSpinner} size="lg" color="white" />
                  <SearchText>Loading...</SearchText>
                </>
              ) : (
                <>
                  <FontAwesomeIcon
                    icon={faSearch}
                    size="lg"
                    color={areRequiredFieldsComplete() ? 'white' : 'var(--color-border)'}
                  />
                  <SearchText>Search</SearchText>
                </>
              )}
            </Button>
          )}
        </SectionContainer>
      </CardContainer>
      <DateRangeModal
        isOpen={isDateModalOpen}
        onClose={() => setDateModalOpen(false)}
        startDate={startDate}
        endDate={endDate}
        onSave={(start, end, pick) => {
          setStartDate(start);
          setEndDate(end);
          setTripMode(pick.mode);
          setMonths(pick.months);
          setDateModalOpen(false);
        }}
        allowMonths={monthlyRentalOffered}
        initialMode={tripMode}
        initialMonths={months}
        minDate={
          // If today has no available times, set min date to tomorrow
          !isTodayAvailable
            ? (() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                return tomorrow;
              })()
            : undefined
        }
      />
    </Container>
  );
}
